import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';

import { Alert, Box, Heading, Paragraph } from '@zillow/constellation-10';

import UnitOverviewCard from './components/UnitOverviewCard';

import type { UserPropertyMultiplexCreateUnitResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  hasPartialUnitsSucceeded?: boolean;
  unitsCreated: Array<UserPropertyMultiplexCreateUnitResponseWebDto>;
  compact?: boolean;
  onActiveUnitIndexChange?: (activeUnitIndex: number | null) => void;
};

export type MultiplexUnitsAddedRef = {
  getActiveUnitListingAlias: () => string | null;
  goToSelectedUnitListingCreation: () => void;
};

export const MultiplexUnitsAdded = forwardRef<MultiplexUnitsAddedRef, Props>(
  ({ hasPartialUnitsSucceeded = false, unitsCreated, compact = false, onActiveUnitIndexChange }, ref) => {
    const [activeUnitIndex, setActiveUnitIndex] = useState<number | null>(null);

    const activeUnit = useMemo(() => {
      return activeUnitIndex !== null ? unitsCreated[activeUnitIndex] : null;
    }, [unitsCreated, activeUnitIndex]);

    /**
     * expose methods to the parent component
     */
    useImperativeHandle(ref, () => ({
      getActiveUnitListingAlias,
      goToSelectedUnitListingCreation,
    }));

    const getActiveUnitListingAlias = () => {
      return activeUnit?.listingAlias ?? null;
    };

    const goToSelectedUnitListingCreation = () => {
      if (activeUnit) {
        window.location.href = `/rental-manager/properties/${activeUnit.listingAlias}/create-listing`;
      } else {
        throw new Error('No active unit selected');
      }
    };

    const handleUnitClick = (index: number) => {
      setActiveUnitIndex(index);
      onActiveUnitIndexChange?.(index);
    };

    return (
      <Box
        data-testid="MultiplexUnitsAdded"
        css={{
          paddingLeft: compact ? '0' : '1200',
          paddingRight: compact ? '0' : '1200',
          paddingTop: '400',
          paddingBottom: '400',
        }}
      >
        <Box data-testid="TitleAndIntro" css={{ marginBottom: '600' }}>
          <Heading level={4} css={{ marginBottom: '200' }}>
            Units added
          </Heading>
          <Paragraph textStyle="body">
            Choose a unit to add listing details and publish — Add more later if you need to.
          </Paragraph>
        </Box>

        {hasPartialUnitsSucceeded && (
          <Alert
            data-testid="PartialUnitsSucceededAlert"
            role="status"
            tone="info"
            css={{ marginTop: '200', marginBottom: '600' }}
          >
            Some of the units you added could not be saved. Only the units below were added.
          </Alert>
        )}

        <Box
          data-testid="UnitOverviewCard-Container"
          css={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '200' }}
        >
          {unitsCreated.map((unit, index) => (
            <UnitOverviewCard
              key={index}
              unit={unit}
              isActive={activeUnitIndex === index}
              onClick={() => handleUnitClick(index)}
            />
          ))}
        </Box>
      </Box>
    );
  },
);

MultiplexUnitsAdded.displayName = 'MultiplexUnitsAdded';

export default MultiplexUnitsAdded;
